import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DecimalPipe, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  EMAIL_CREDITS_PER_FOUND,
  LeadExport,
  ProspectingService,
  apiErrorMessage,
  continuationTarget,
  exportLeadsCostFrom,
} from '../../../../services/prospecting.service';
import { PopupService } from '../../../../services/popup.service';

/**
 * Continue an export that stopped because credits ran out: a new part picks
 * up the list where it stopped, with the same columns, priced as the rest of
 * one uninterrupted export.
 */
@Component({
  selector: 'app-continue-export-dialog',
  standalone: true,
  imports: [NgIf, FormsModule, DecimalPipe],
  templateUrl: './continue-export-dialog.component.html',
})
export class ContinueExportDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) exp!: LeadExport;
  /** Current balance, when the host knows it. */
  @Input() balance: number | null = null;
  @Output() started = new EventEmitter<LeadExport>();
  @Output() closed = new EventEmitter<void>();

  includeEmails = false;
  isStarting = false;
  errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    private prospectingService: ProspectingService,
    private popupService: PopupService,
  ) {}

  ngOnInit(): void {
    this.includeEmails = !!this.exp.include_emails;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get offset(): number {
    return (this.exp.lead_offset ?? 0) + this.exp.leads_found;
  }

  get target(): number {
    return continuationTarget(this.exp);
  }

  get nextPart(): number {
    return (this.exp.part ?? 1) + 1;
  }

  get emailColumn(): boolean {
    return !this.exp.columns || this.exp.columns.includes('email');
  }

  get withEmails(): boolean {
    return this.includeEmails && this.emailColumn;
  }

  get leadsCost(): number {
    return exportLeadsCostFrom(this.offset, this.target);
  }

  get emailsCost(): number {
    return this.withEmails ? EMAIL_CREDITS_PER_FOUND * this.target : 0;
  }

  get startCost(): number {
    return exportLeadsCostFrom(this.offset, Math.min(this.target, 10)) + (this.withEmails ? EMAIL_CREDITS_PER_FOUND : 0);
  }

  get cannotStart(): boolean {
    return this.balance !== null && this.balance < this.startCost;
  }

  get shortOfCredits(): boolean {
    return this.balance !== null && this.balance < this.leadsCost + this.emailsCost;
  }

  close(): void {
    if (!this.isStarting) {
      this.closed.emit();
    }
  }

  start(): void {
    if (this.isStarting) {
      return;
    }
    this.errorMessage = '';
    this.isStarting = true;

    this.prospectingService.continueLeadExport(this.exp.id, this.withEmails)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ export: next }) => {
          this.isStarting = false;
          this.popupService.notify(`Export continued (part ${next.part ?? this.nextPart}) — the file lands on the Exports page.`, '', 'success');
          this.started.emit(next);
        },
        error: (error) => {
          this.isStarting = false;
          const code = error?.error?.error;
          if (error?.status === 409 && code === 'already_continued' && error?.error?.export) {
            this.popupService.notify('This export was already continued.', '', 'info');
            this.started.emit(error.error.export as LeadExport);
            return;
          }
          if (error?.status === 409 && code === 'export_in_progress') {
            this.errorMessage = 'You already have a lead export running. Wait for it to finish or cancel it.';
            return;
          }
          if (error?.status === 402) {
            const required = error?.error?.required;
            this.errorMessage = required
              ? `Not enough credits — continuing needs ${required} credits to start.`
              : 'Not enough credits to continue this export.';
            return;
          }
          this.errorMessage = apiErrorMessage(error, 'Could not continue the export. Please try again.');
        }
      });
  }
}
